import { Button, Col, Row, Tag, Tooltip } from "antd";
import "ckeditor5/ckeditor5-content.css";
import "ckeditor5/ckeditor5.css";
import moment from "moment";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { BsFillSendFill, BsSave, BsTrash3Fill } from "react-icons/bs";
import {
  IoIosArrowDown,
  IoIosArrowUp,
  IoIosEyeOff,
  IoMdArrowDropdown,
  IoMdArrowDropright,
  IoMdClose,
  IoMdDownload,
} from "react-icons/io";
import { MdFilePresent } from "react-icons/md";
import { v4 as uuidv4 } from "uuid";
import apiFactory from "../../../api";
import { CustomAvatar } from "../../../components/avatar/CustomAvatar";
import { DraftMessageComponent } from "../../../components/chat/DraftMessageComponent";
import FileHandlerWrapper from "../../../components/mail/FileHandlerWrapper";

import { ForwardMailModal } from "../../../components/modal/ForwardMailModal";
import { GeneralModal } from "../../../components/modal/GeneralModal";
import { PreviewFileUploadModal } from "../../../components/modal/PreviewFileUploadModal";
import { MailFilesPreview } from "../../../components/preview/MailFilesPreview";
import EmailComposer from "../../../components/richTextEditor/EmailComposer";
import { conversationType } from "../../../config/Constant";
import { useMenuContext } from "../../../context/MenuContext";
import { useInfoUser } from "../../../store/UserStore";
import { formatShortTime, formatTime } from "../../../utils/formatTime";

import Icon from "@ant-design/icons";
import { uniq, uniqBy } from "lodash";
import { LuAlarmClock, LuForward, LuReply, LuReplyAll } from "react-icons/lu";
import { RESOURCE_API } from "../../../api/apiConstant";
import { useGlobalDownloader } from "../../../context/global/DownloadProvider";
import { MailScheduleModal } from "../../../components/modal/mail/MailScheduleModal";
import MailStorageFile from "../../../components/preview/MailStorageFile";
import { SSendIcon } from "../../../config/Icon";
import { useAuthLayoutContext } from "../../../context/AuthLayoutContext";
import { useDraftMailContext } from "../../../context/DraftMailContext";
import { useMailContext } from "../../../context/MailContext";
import {
  blobToBase64,
  convertOembedToIframe,
  convertToUrl,
  extractEditorResourceIds,
  getPlainTextFromHtml,
  openNotificationError,
  openNotificationSuccess,
} from "../../../utils/Utils";
import { RecipientSummary } from "./MailDraft";
import "./style.scss";

const ShowFile = ({ notification }) => {
  const createdAt = notification?.createdAt;
  const resources = notification?.resources || notification?.files || [];
  const [pickedList, setPickedList] = useState([]);

  const {
    downloads,
    startDownload,
    cancelDownload,
    pauseDownload,
    resumeDownload,
    DOWNLOAD_STATUS,
  } = useGlobalDownloader();

  const pickFile = (resourceId) => {
    if (!resourceId) return;

    if (pickedList.includes(resourceId)) {
      setPickedList(pickedList.filter((id) => id !== resourceId));
    } else {
      setPickedList([...pickedList, resourceId]);
    }
  };

  const handleDownload = async (e) => {
    e.stopPropagation();

    const result = await apiFactory.resourceApi.prepareZip(pickedList);

    if (result?.status !== 200) {
      openNotificationError("Prepare files for download failed");
    }

    startDownload(result?.data, "willo.zip");
  };

  return (
    <div>
      {pickedList?.length > 0 ? (
        <div className="flex gap-[20px] ml-[50px] mb-[5px] bg-[#f0f4f9] p-[5px] rounded-[10px]">
          <button onClick={() => setPickedList([])}>
            <IoMdClose className="text-[18px] text-[#2b9853]" />
          </button>
          <div>{pickedList?.length} selected</div>
          <button onClick={handleDownload}>
            <IoMdDownload className="text-[20px] text-[#2b9853]" />
          </button>
        </div>
      ) : (
        <div className="h-[36px]"></div>
      )}
      {resources?.length > 0 && (
        <Row className="ml-[37px] mb-2">
          {resources?.map((item) => {
            const fileWithTimestamp = {
              ...item,
              createdAt,
            };

            return (
              <Col
                className="flex justify-center"
                key={item?.resourceId}
                xs={24}
                sm={24}
                md={24}
                lg={12}
                xl={12}
                xxl={6}
              >
                <MailStorageFile
                  file={fileWithTimestamp}
                  pickFile={pickFile}
                  pickedList={pickedList}
                  setPickedList={setPickedList}
                  key={item?.resourceId}
                  senderId={notification?.senderId}
                />
              </Col>
            );
          })}
        </Row>
      )}
    </div>
  );
};

const RecipientCount = ({ notification }) => {
  const { orgList } = useAuthLayoutContext();
  const {
    selectedMessage,
    setSelectedMessage,
    isModalMembersReadUnread,
    setIsModalMembersReadUnread,
  } = useMailContext();

  const recipientRepresentatives = useMemo(() => {
    const filtered = orgList?.filter((user) =>
      notification?.recipientRepresentatives?.includes(user.userCode),
    );

    return uniqBy(filtered, "userId").map((user) => user?.name);
  }, [orgList, notification?.recipientRepresentatives]);

  const ccRepresentatives = useMemo(() => {
    const filtered = orgList?.filter((user) =>
      notification?.ccRepresentatives?.includes(user.userCode),
    );

    return uniqBy(filtered, "userId").map((user) => user?.name);
  }, [orgList, notification?.ccRepresentatives]);

  const formatCountText = (label, representatives = [], total = 0) => {
    if (total <= 0) return null;

    const names = representatives.join(", ");
    const remaining = total - representatives.length;

    if (!names) return `${label}: ${total}`;
    if (remaining <= 0) return `${label}: ${names}`;

    return `${label}: ${names} +${remaining}`;
  };

  return (
    <button
      className="text-[#5e5e5e] text-[12px] text-left"
      onClick={(e) => {
        e.stopPropagation();
        setIsModalMembersReadUnread(true);
        setSelectedMessage(notification);
      }}
    >
      {formatCountText(
        "Recipient",
        recipientRepresentatives,
        notification?.recipientCount,
      ) && (
        <div>
          {formatCountText(
            "Recipient",
            recipientRepresentatives,
            notification?.recipientCount,
          )}
        </div>
      )}

      {formatCountText(
        "Reference",
        ccRepresentatives,
        notification?.ccCount,
      ) && (
        <div>
          {formatCountText(
            "Reference",
            ccRepresentatives,
            notification?.ccCount,
          )}
        </div>
      )}
    </button>
  );
};

const Topic = ({ topic }) => {
  const [openOut, setOpenOut] = useState(false);

  const { setIsModalMembersReadUnread } = useMailContext();

  return (
    <div className="flex flex-col gap-[10px] ml-[30px] border-[#e5e7eb] p-[10px]">
      {!openOut && (
        <button
          className="flex justify-between items-center gap-[2px] text-[#2a56b9] font-semibold mb-[15px]"
          onClick={() => setOpenOut(true)}
        >
          <div className="flex justify-center gap-[2px]">
            <button className="flex gap-[2px]">
              <IoIosArrowDown size={20} />
              <div>Show conversation({topic?.histories?.length})</div>
            </button>
          </div>
        </button>
      )}

      {openOut && (
        <div className="flex flex-col gap-[10px]">
          <button
            className="flex justify-between items-center gap-[2px] text-[#2a56b9] font-semibold"
            onClick={() => setOpenOut(false)}
          >
            <div className="flex justify-center gap-[2px]">
              <button className="flex gap-[2px]">
                <IoIosArrowUp size={20} />
                <div>Hide conversation({topic?.histories?.length})</div>
              </button>
            </div>
          </button>

          {topic?.histories?.map((history, index) => (
            <MailReply
              notification={history}
              setIsModalMembersReadUnread={setIsModalMembersReadUnread}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const MailReply = forwardRef(
  (
    {
      notification,
      isLastItem,
      isTopMessage,
      addMemberOrg,
      onDirtyChange,
      onSentSuccess,
    },
    ref,
  ) => {
    const [mailContent, setMailContent] = useState({
      content: "",
      oldContent: "",
    });

    const [schedule, setSchedule] = useState({
      time: notification?.draftMail?.scheduleTime
        ? notification?.draftMail?.scheduleTime
        : "",
      triggerId: notification?.draftMail?.triggerId
        ? notification?.draftMail?.triggerId
        : uuidv4(),
      isUpdateScheduleTime: false,
    });
    const [isReply, setIsReply] = useState(false);
    const [isForwardMail, setIsForwardMail] = useState(false);
    const {
      selectedConversation,
      handleUpdateConversationList,
      handleChangeConversation,
      onBackToConversations,
      conversationList,
    } = useMenuContext();

    const {
      selectedMessage,
      setSelectedMessage,
      isModalMembersReadUnread,
      setIsModalMembersReadUnread,
    } = useMailContext();

    const { cCOrgList, memberOrgList, cleanMemberList, setIsReplyAll } =
      useDraftMailContext();
    const { orgList } = useAuthLayoutContext();

    const {
      topMessageList,
      setTotalNotificationItem,
      bottomMessageList,
      setBottomMessageList,
      setTopMessageList,
      title,
      setIsLoading,
      isLoading,
    } = useMailContext();
    const { user } = useInfoUser();
    const replyDivRef = useRef(null);
    const { languageMap } = useInfoUser();
    const [isOpenModalConfirmation, setIsOpenModalConfirmation] =
      useState(false);
    const [isDeleteMailDraft, setIsDeleteMailDraft] = useState(false);
    const [processedContent, setProcessedContent] = useState("");
    const [previewFiles, setPreviewFiles] = useState([]);
    const [uploadFiles, setUploadFiles] = useState([]);
    const [isOpenOut, setIsOpenOut] = useState(true);
    const [isOpenUploadModal, setIsOpenUploadModal] = useState(false);
    const [isMailScheduleModal, setIsMailScheduleModal] = useState(false);

    const clearAllBeforeEvent = () => {
      setMailContent({ content: "", oldContent: "" });
      setPreviewFiles([]);
      setUploadFiles([]);
      setIsReply(false);
      setIsLoading(false);
      cleanMemberList();
    };

    const changeOpenUp = () => {
      setIsOpenOut(!isOpenOut);
    };

    const createNotification = async () => {
      if (!validateSendMail(true)) return;

      const { userIds, ccIds } = addMemberOrg();

      if (
        !selectedConversation?.conversationId ||
        selectedConversation?.type === conversationType.DRAFT ||
        isForwardMail
      ) {
        if (!userIds || userIds?.length === 0) {
          openNotificationError(
            languageMap
              ? languageMap["error.chosenRecipient"]
              : "You haven't chosen recipient",
          );

          setIsLoading(false);
          return;
        }
      }

      const params = {
        name: isForwardMail ? "" : title,
        userIds,
        ccIds,
        conversationType: conversationType.NOTIFICATION,
        conversationId: isForwardMail
          ? null
          : selectedConversation?.conversationId || null,
      };

      try {
        const result = await apiFactory.conversationApi.createMailGroup(params);

        if (result.status !== 200) {
          openNotificationError(languageMap?.[result?.message]);
        } else {
          const conversationRes = result?.data;
          await sendMail(conversationRes?.conversationId);
          onCreateNotificationSuccessfully(conversationRes);
        }
      } catch (error) {
      } finally {
        clearAllBeforeEvent();
      }
    };

    const onCreateNotificationSuccessfully = (conversation) => {
      handleChangeConversation(conversation);
    };

    const validateSendMail = (isNoti) => {
      if (
        mailContent?.content?.trim()?.length === 0 &&
        previewFiles?.length === 0
      ) {
        if (isNoti) {
          openNotificationError(
            languageMap?.["error.mailHaveContent"] ??
              "You have to input email content",
          );
        }

        setIsLoading(false);
        return false;
      }

      return true;
    };

    const processHtmlForSaving = (htmlContent) => {
      if (!htmlContent) return "";

      const doc = new DOMParser().parseFromString(htmlContent, "text/html");

      const figures = Array.from(
        doc.querySelectorAll("figure[data-resource-id]"),
      );

      figures.forEach((figureDoc) => {
        const resourceId = figureDoc.getAttribute("data-resource-id");

        if (resourceId) {
          const imgDoc = figureDoc.querySelector("img");
          if (!imgDoc) return;

          const apiUrl = `${process.env.REACT_APP_AUTHENTICATION_API_URL}/${RESOURCE_API}/free/file/${resourceId}`;

          imgDoc.src = apiUrl;

          figureDoc.removeAttribute("data-resource-id");
        }
      });

      const otherImages = Array.from(doc.querySelectorAll("img"));
      otherImages.forEach((imgDoc) => {
        if (
          isBase64(imgDoc.src) &&
          !imgDoc.closest("figure[data-resource-id]")
        ) {
          const resourceId = imgDoc.getAttribute("data-resource-id");

          if (!imgDoc) return;

          const apiUrl = `${process.env.REACT_APP_AUTHENTICATION_API_URL}/${RESOURCE_API}/free/file/${resourceId}`;

          imgDoc.src = apiUrl;

          imgDoc.removeAttribute("data-resource-id");
        }
      });

      return doc.body.innerHTML;
    };

    const filterMemberOrg = () => {
      let userIds = memberOrgList
        ?.filter((org) => org?.type === "PERSON" && org?.checked)
        ?.map((org) => org?.objectId);
      userIds = uniq(userIds);

      let ccIds = cCOrgList
        ?.filter((org) => org?.type === "PERSON" && org?.checked)
        ?.map((org) => org?.objectId);
      ccIds = uniq(ccIds);

      return { userIds, ccIds };
    };

    const sendMail = async (creatingConversationId) => {
      if (!validateSendMail(true)) return;

      const requestUuid = uuidv4();

      const conversationId =
        notification?.conversationId && !isForwardMail
          ? notification.conversationId
          : creatingConversationId;

      const allEditorResourceIds = extractEditorResourceIds(
        mailContent?.content,
      );

      const { userIds, ccIds } = filterMemberOrg();

      if (userIds?.length === 0 && ccIds?.length === 0) {
        openNotificationError(
          languageMap
            ? languageMap["error.chosenRecipient"]
            : "You haven't chosen recipient",
        );
        setIsLoading(false);
        return;
      }

      const params = {
        requestUuid,
        conversationId,
        recipientIds: userIds,
        ccIds: ccIds,
        content: processHtmlForSaving(mailContent?.content),
        isDraft: false,
        files: previewFiles || [],
        updatedMediaConversationId: true,
        draftMailId: notification?.draftMail?.draftMailId,
        mailId: notification?.mailId,
        editorResourceId: allEditorResourceIds,
        plainText: getPlainTextFromHtml(mailContent?.content),
        isForwardMail: false,
      };

      try {
        const rs = await apiFactory.mailApi.sendMessage(params);

        if (rs && rs.status !== 200) {
          openNotificationError("Create mail unsuccessfully");
          return;
        }

        const message = {
          ...params,
          draftMail: null,
          isDraft: false,
          isSelf: true,
          updatedAt: moment(),
          createdAt: moment(),
        };

        setTotalNotificationItem((prev) => prev + 1);
        onDirtyChange(false);

        updateMessage(message);
        clearAllBeforeEvent();
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    const sendNotification = async () => {
      setIsLoading(true);

      await sendMail();
      onSentSuccess?.();
    };

    const sendNotificationV2 = async () => {
      const { userIds, ccIds } = filterMemberOrg();

      try {
        const rs = await apiFactory.letterApi.send({
          previousLetterId: notification?.letterId,
          requestUuid: uuidv4(),
          recipientIds: userIds,
          ccIds,
          conversationType: conversationType.NOTIFICATION,
          conversationId: notification?.conversationId,
          plainText: getPlainTextFromHtml(mailContent?.content),

          content: processHtmlForSaving(mailContent?.content),
          isDraft: false,
          files: previewFiles || [],
          // updatedMediaConversationId: true,
          draftMailId: notification?.draftMail?.draftMailId,
          // mailId: notification?.mailId,
          // editorResourceId: extractEditorResourceIds(mailContent?.content),
          // isForwardMail: false,
        });

        if (rs && rs.status !== 200) {
          openNotificationError("Create mail unsuccessfully");
          return;
        }

        onSentSuccess?.();
        clearAllBeforeEvent();
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    const sendSchedule = async () => {
      if (!mailContent?.content && previewFiles?.length === 0) return;

      const selectedConversationId = notification?.conversationId;
      const { userIds, ccIds } = filterMemberOrg();

      const req = {
        conversationId: selectedConversationId,
        // mailId: notification?.mailId,
        // letterId: notification?.letterId,
        previousLetterId: notification?.letterId,
        recipientIds: userIds,
        ccIds: ccIds,
        content: mailContent?.content,

        scheduleTime: schedule?.time,
        triggerId: schedule?.triggerId,
        isUpdateScheduleTime: schedule?.isUpdateScheduleTime,

        files: previewFiles || [],
        requestUuid: uuidv4(),
        updatedMediaConversationId: false,
      };

      const draftMessage = await apiFactory.draftMessageApi.createOrUpdate(req);

      if (draftMessage && draftMessage.status === 200 && draftMessage.data) {
        setIsReply(false);
        onDirtyChange(false);
        updateDraftMessage(draftMessage?.data);
      } else {
        openNotificationError("Save or update draft messages failed");
      }
    };

    const getName = useCallback(
      (id) => {
        if (id === "ALL") {
          return languageMap?.["modal.notification.replyAll"] ?? "all";
        } else {
          return orgList?.find((org) => org?.userCode === id)?.name;
        }
      },
      [orgList, languageMap],
    );

    const isBase64 = (str) => {
      return str.startsWith("data:image/") && str.includes(";base64");
    };

    const getSender = () => {
      return orgList?.find((org) => org?.userCode === notification?.senderId);
    };

    const getPosition = () => {
      if (replyDivRef.current) {
        replyDivRef.current.scrollIntoView({
          block: "center",
          behavior: "instant",
        });
      }
    };

    const getIconNotification = (notification) => {
      if (!notification?.files?.length) return;

      return (
        <div className="text-[#5e5e5e] justify-end items-end">
          <MdFilePresent size={20} color={"#3269c9"} />
        </div>
      );
    };

    const GetIconUnread = ({ notification, onClick }) => {
      if (notification?.oldConversationId) return null;

      return (
        <Button
          className="bg-[#2a56b9] text-white h-[20px] gap-[4px] zoom-09 flex items-center"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onClick?.(e);
          }}
        >
          <IoIosEyeOff size={12} />
          <span className="text-[12px]">{notification?.unreadCount}</span>
        </Button>
      );
    };

    const showOpenDownArrow = useMemo(() => {
      return (
        <div
          onClick={changeOpenUp}
          onMouseDown={(e) => {
            e.preventDefault();
          }}
          className="cursor-pointer"
        >
          {isOpenOut ? (
            <Tooltip
              placement="top"
              title={languageMap?.["modal.message.showLess"] ?? "show less"}
              color={"#0091ff"}
            >
              <IoMdArrowDropdown size={20} color="black" />
            </Tooltip>
          ) : (
            <Tooltip
              placement="top"
              title={languageMap?.["modal.message.showMore"] ?? "show more"}
              color={"#0091ff"}
            >
              <IoMdArrowDropright size={20} color="black" />
            </Tooltip>
          )}
        </div>
      );
    }, [isOpenOut]);

    const handleDeleteMailDraft = async () => {
      const { conversationId, draftMail } = notification || {};
      const draftMailId = draftMail?.draftMailId;

      if (!conversationId || !draftMailId) {
        setIsDeleteMailDraft(false);
        return;
      }

      try {
        const rs = await apiFactory.draftMessageApi.delete({
          conversationId,
          draftMailId,
          type: conversationType.DRAFT,
        });

        if (rs?.status !== 200) {
          openNotificationError("Delete Mail Draft Fail!");
          return;
        }

        const newConvs = conversationList.filter(
          (c) => c?.conversationId !== selectedConversation?.conversationId,
        );

        handleUpdateConversationList(newConvs);

        onBackToConversations();

        openNotificationSuccess("Delete Mail Draft Successfully!");
      } catch (error) {
        openNotificationError("Delete Mail Draft Error!");
        console.error(error);
      } finally {
        setIsDeleteMailDraft(false);
      }
    };

    const handleCloseReply = async () => {
      if (validateSendMail(false)) {
        setIsOpenModalConfirmation(true);
      } else {
        await deleteDraftMessage();

        clearAllBeforeEvent();
      }
    };

    const handleSaveOrUpdateDraftMessage = async () => {
      if (!mailContent?.content && previewFiles?.length === 0) return;

      const { userIds, ccIds } = filterMemberOrg();
      const selectedConversationId = notification?.conversationId;

      const req = {
        conversationId: selectedConversationId,
        // letterId: notification?.letterId,
        // mailId: notification?.mailId,
        previousLetterId: notification?.letterId,
        content: mailContent?.content,
        recipientIds: userIds,
        ccIds: ccIds,
        files: previewFiles || [],
        requestUuid: uuidv4(),
        updatedMediaConversationId: false,
        isUpdateScheduleTime: notification?.draftMail?.triggerId ? true : false,
        scheduleTime: null,
      };

      const draftMessage = await apiFactory.draftMessageApi.createOrUpdate(req);

      if (draftMessage?.status === 200 && draftMessage?.data) {
        setIsReply(false);
        onDirtyChange(false);
        updateDraftMessage(draftMessage?.data);
        setSchedule({
          time: "",
          triggerId: uuidv4(),
          isUpdateScheduleTime: false,
        });
      } else {
        openNotificationError("Save or update draft messages failed");
      }
    };

    useImperativeHandle(ref, () => ({
      saveDraft: handleSaveOrUpdateDraftMessage,
      updateSchedule: sendSchedule,
    }));

    const extractImageIds = (htmlString) => {
      if (!htmlString) return [];
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, "text/html");

      const elements = doc.querySelectorAll("[data-resource-id]");

      const ids = Array.from(elements)
        .map((el) => {
          return el.getAttribute("data-resource-id");
        })
        .filter((id) => id);

      return [...new Set(ids)];
    };

    const deleteDraftMessage = async () => {
      if (
        !isTopMessage &&
        (!notification ||
          !notification.draftMail ||
          !notification.draftMail.draftMailId)
      )
        return;

      if (notification?.draftMail?.draftMailId) {
        const rs = await apiFactory.draftMessageApi.delete({
          conversationId: notification?.conversationId,
          draftMailId: notification?.draftMail?.draftMailId,
          mailId: notification?.mailId,
        });

        if (rs?.status !== 200) {
          openNotificationError("Delete draft messages failed");
          return;
        }
      }
      updateMessage(null);
    };

    const handleDeleteImageEditor = async () => {
      const deletedIds = extractImageIds(mailContent?.content);

      await apiFactory.resourceApi
        .deleteFiles({
          resourceIds: deletedIds,
          conversationId: notification?.conversationId,
        })
        .catch((err) => console.error("Failed to delete files", err));
    };

    const handleConfirmCloseReply = async () => {
      setIsReply(false);
      setIsOpenModalConfirmation(false);
      await handleDeleteImageEditor();
      await deleteDraftMessage();
      clearAllBeforeEvent();
    };

    const updateDraftMessageOnTop = (data) => {
      if (!topMessageList?.length) return false;

      const index = topMessageList?.findIndex(
        (msg) => msg?.letterId === notification?.letterId,
      );

      if (index === -1) return false;

      // topMessageList[index].letterId = data?.letterId;
      topMessageList[index].isEditing = false;
      topMessageList[index].draftMail = data;
      setTopMessageList([...topMessageList]);

      return true;
    };

    const updateDraftMessageOnBottom = (data) => {
      if (!bottomMessageList?.length) return;

      const index = bottomMessageList?.findIndex(
        (msg) => msg?.letterId === notification?.letterId,
      );

      if (index !== -1) {
        // bottomMessageList[index].letterId = data?.letterId;
        bottomMessageList[index].isEditing = false;
        bottomMessageList[index].draftMail = data;
        setBottomMessageList([...bottomMessageList]);
      }
    };

    const updateDraftMessage = (data) => {
      const updateTop = updateDraftMessageOnTop(data);
      if (!updateTop) updateDraftMessageOnBottom(data);
    };

    const updateMessageOnTop = () => {
      if (!topMessageList) return;

      const index = topMessageList?.findIndex(
        (msg) => msg?.requestUuid === notification?.requestUuid,
      );

      if (index === -1) return;

      if (topMessageList?.[index]?.isDraft) {
        topMessageList?.splice(0, 1);
        setTopMessageList([...topMessageList]);
      } else {
        topMessageList[index].draftMail = null;
        setTopMessageList([...topMessageList]);
      }
    };

    const updateMessageOnBottom = (data) => {
      if (!bottomMessageList) return;

      const index = bottomMessageList?.findIndex(
        (msg) => msg?.requestUuid === notification?.requestUuid,
      );

      if (index !== -1) {
        bottomMessageList[index].draftMail = null;
      }

      if (data) {
        setBottomMessageList([...bottomMessageList, data]);
      }
    };

    const updateMessage = (data) => {
      updateMessageOnTop(data);
      updateMessageOnBottom();
      setSchedule({
        time: "",
        triggerId: uuidv4(),
        isUpdateScheduleTime: false,
      });
    };

    const closeDraftMessage = (isClose) => {
      let newMessageList = [];

      if (isTopMessage) {
        const index = topMessageList?.findIndex(
          (n) => n?.mailId === notification?.mailId,
        );

        if (index !== -1) {
          newMessageList = topMessageList?.map((item, i) =>
            i === index
              ? {
                  ...item,
                  draftMail: {
                    ...item?.draftMail,
                    isClosed: isClose,
                  },
                }
              : item,
          );
        } else {
          newMessageList = topMessageList;
        }

        setTopMessageList([...newMessageList]);
      } else {
        const index = bottomMessageList?.findIndex(
          (n) => n?.mailId === notification?.mailId,
        );

        if (index !== -1) {
          newMessageList = bottomMessageList?.map((item, i) =>
            i === index
              ? {
                  ...item,
                  draftMail: {
                    ...item?.draftMail,
                    isClosed: isClose,
                  },
                }
              : item,
          );
        } else {
          newMessageList = bottomMessageList;
        }

        setBottomMessageList([...newMessageList]);
      }
    };

    const convertedContent = useMemo(() => {
      let html = processedContent;

      html = convertOembedToIframe(html);
      html = convertToUrl(html);

      return html;
    }, [processedContent]);

    const handleCancelUpload = () => {
      setIsOpenUploadModal(false);
      setUploadFiles([]);
    };

    const cancelForward = () => {
      setIsForwardMail(false);
    };

    useEffect(() => {
      const processReceivedImages = async () => {
        let content = notification?.content;
        if (!content) {
          setProcessedContent("");
          return;
        }

        const doc = new DOMParser().parseFromString(content, "text/html");
        const images = Array.from(doc.querySelectorAll("img"));

        const imagePromises = images.map(async (imgDoc) => {
          if (!imgDoc?.src) return Promise.resolve();

          if (isBase64(imgDoc.src)) return Promise.resolve();

          if (!imgDoc.src.includes(RESOURCE_API)) {
            return Promise.resolve();
          }

          const parts = imgDoc.src.split("/");
          const resourceId = parts.pop();
          if (!resourceId || resourceId === "null") {
            return Promise.resolve();
          }

          try {
            const fetchedBlob =
              await apiFactory.resourceApi.getStreamFileAsBlob(resourceId);

            if (
              !fetchedBlob ||
              fetchedBlob.size === 0 ||
              fetchedBlob.type.startsWith("application/json")
            ) {
              imgDoc.remove();
              return;
            }

            const base64Data = await blobToBase64(fetchedBlob);

            imgDoc.src = base64Data;
          } catch (error) {
            console.error("Failed to load received image:", resourceId, error);
            imgDoc.remove();
          }
        });

        await Promise.all(imagePromises);

        setProcessedContent(doc.body.innerHTML);
      };

      processReceivedImages();
    }, [notification?.content]);

    useEffect(() => {
      setIsReply(notification?.isEditing);
    }, [notification?.isEditing]);

    useEffect(() => {
      if (!notification?.draftMail) {
        setIsReply(false);
        setSchedule({
          time: "",
          triggerId: uuidv4(),
          isUpdateScheduleTime: false,
        });
      }
    }, [notification?.draftMail]);

    useEffect(() => {
      if (isReply) {
        getPosition();
      }
    }, [isReply]);

    useEffect(() => {
      if (notification?.draftMail?.files) {
        setPreviewFiles((prev) => {
          const oldIds = prev?.map((f) => f?.resourceId) || [];
          const newFiles = notification?.draftMail?.files?.filter(
            (f) => !oldIds?.includes(f?.resourceId),
          );
          return [
            ...prev,
            ...newFiles.map((f) => ({ ...f, fileDraftOld: true })),
          ];
        });
      }
    }, [notification?.draftMail?.files]);

    useEffect(() => {
      if (convertedContent) {
        setIsOpenOut(isLastItem ?? false);
      }
    }, [convertedContent]);

    useEffect(() => {
      if (selectedConversation?.type === conversationType.DRAFT) {
        setMailContent({ content: "", oldContent: "" });
        setPreviewFiles(
          selectedConversation?.conversationId
            ? notification?.draftMail?.files.map((file) => ({
                ...file,
                fileDraftOld: true,
              }))
            : [],
        );
        setUploadFiles([]);
      }
    }, [selectedConversation]);

    useEffect(() => {
      if (!isReply) {
        onDirtyChange?.(false);
        return;
      }

      const dirty =
        (notification?.draftMail?.content !== mailContent?.content &&
          mailContent?.content !== mailContent?.oldContent) ||
        previewFiles?.some((f) => !f.fileDraftOld);

      onDirtyChange?.(dirty);
    }, [mailContent, previewFiles, isReply, notification?.draftMail]);

    return (
      <div className="reply-box">
        {!notification?.isDraft && (
          <div key={notification?.notificationId}>
            <div
              className="flex justify-between cursor-pointer"
              // onClick={changeOpenUp}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
            >
              <div className="flex items-center gap-[6px] mb-2">
                <div onClick={(e) => e.stopPropagation()}>
                  <CustomAvatar
                    person={getSender()}
                    userId={getSender()?.userId}
                    styleCss={{}}
                    isShowDetail={true}
                    isShowUserStatus={{ status: true, isAlwaysTrue: false }}
                  />
                </div>

                <div className="mr-2">
                  <div className="text-[14px] flex items-center">
                    <div className="font-bold">
                      {getName(notification?.senderId)}
                    </div>
                    {/*<div className="ml-2">{showOpenDownArrow}</div>*/}
                  </div>
                  <div className="text-[#5e5e5e] text-[12px]">
                    <RecipientCount
                      notification={notification}
                      setIsModalMembersReadUnread={setIsModalMembersReadUnread}
                      setSelectedMessage={setSelectedMessage}
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col mr-[32px] items-end">
                <div className="flex items-center gap-[10px] h-[36px]">
                  {notification?.isForward && (
                    <Tag color="#ffa500">forwarded</Tag>
                  )}

                  {notification?.role &&
                    selectedConversation?.unreadCount > 0 &&
                    (!selectedConversation?.lastSeenTs ||
                      selectedConversation?.lastSeenTs <
                        notification?.createdAt) && (
                      <div className="bg-[#188038] text-white h-[20px] gap-[4px] zoom-09 flex items-center justify-center rounded-full px-[8px]">
                        <span className="text-[12px]">new</span>
                      </div>
                    )}
                  {notification?.role && (
                    <GetIconUnread
                      notification={notification}
                      onClick={() => {
                        setIsModalMembersReadUnread(true);
                        setSelectedMessage(notification);
                      }}
                    />
                  )}

                  {notification?.role && (
                    <>
                      {notification?.senderId !== user?.userId && (
                        <Tooltip
                          placement="top"
                          title={
                            languageMap?.[
                              "modal.notification.replyToSenderOnly"
                            ] ?? "Reply"
                          }
                          color={"#0091ff"}
                        >
                          <button
                            className="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsReply(true);
                              setIsReplyAll(false);
                              closeDraftMessage(true);
                            }}
                          >
                            <LuReply size={20} color="#88179b" />
                          </button>
                        </Tooltip>
                      )}

                      <Tooltip
                        placement="top"
                        title={
                          languageMap?.["modal.notification.replyAll"] ??
                          "Reply all"
                        }
                        color={"#0091ff"}
                      >
                        <button
                          className="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsReply(true);
                            setIsReplyAll(true);
                            closeDraftMessage(true);
                          }}
                        >
                          <LuReplyAll size={20} color="#88179b" />
                        </button>
                      </Tooltip>

                      <Tooltip
                        placement="top"
                        title={
                          languageMap?.[
                            "modal.notification.forwardToSenderOnly"
                          ] ?? "Reply to sender only"
                        }
                        color={"#0091ff"}
                      >
                        <button
                          className="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsForwardMail(true);
                          }}
                        >
                          <LuForward size={20} color="#2a56b9" />
                        </button>
                      </Tooltip>
                    </>
                  )}
                </div>
                <div className="text-[#5e5e5e] text-[12px] text-right">
                  {formatTime(notification?.createdAt)}
                </div>
              </div>
            </div>
            <div className="ml-[37px] text-[15px] break-words custom-collapsible pb-[10px]">
              <div
                className="ck-content !h-auto"
                dangerouslySetInnerHTML={{
                  __html: convertedContent,
                }}
              />
            </div>
            <div
            // style={{ display: isOpenOut && notification ? "block" : "none" }}
            >
              <ShowFile notification={notification} />
            </div>
            <div className="flex flex-col gap-[10px]">
              {notification?.topics?.map((topic) => {
                return <Topic topic={topic} />;
              })}
            </div>
          </div>
        )}

        {isForwardMail && (
          <ForwardMailModal
            notification={notification}
            isForwardMail={isForwardMail}
            setIsForwardMail={setIsForwardMail}
            addMemberOrg={addMemberOrg}
            cancelForward={cancelForward}
          />
        )}
        {!isReply &&
          notification?.draftMail?.draftMailId &&
          !notification?.isClosed && (
            <div className={`${!notification?.isDraft && "ml-5"}`}>
              <DraftMessageComponent
                draftMessage={notification?.draftMail}
                languageMap={languageMap}
                setIsReply={setIsReply}
                selectedConversation={selectedConversation}
                currentUser={user}
                isReplyAll={false}
              />
            </div>
          )}
        {isReply && (
          <FileHandlerWrapper
            className="mt-2"
            setUploadFiles={setUploadFiles}
            setIsOpenUploadModal={setIsOpenUploadModal}
            languageMap={languageMap}
            innerRef={replyDivRef}
          >
            {selectedConversation?.type !== conversationType.DRAFT && (
              <RecipientSummary />
            )}
            <div className="mt-[20px]">
              <EmailComposer
                originalContent={notification?.draftMail?.content || ""}
                setMailContent={setMailContent}
                mailContent={mailContent}
                conversationId={notification?.conversationId}
              />
            </div>
            <div className="flex flex-col">
              <div className="flex gap-[10px]">
                <MailFilesPreview
                  previewFiles={previewFiles}
                  setPreviewFiles={setPreviewFiles}
                  setIsOpenUploadModal={setIsOpenUploadModal}
                  setUploadFiles={setUploadFiles}
                />
              </div>
              <div className="flex justify-between mt-[25px]">
                <div className="flex gap-[5px] items-center">
                  <Tooltip
                    placement="top"
                    title={
                      languageMap?.["modal.conversation.schedule"] ?? "Schedule"
                    }
                    color={"#0091ff"}
                  >
                    <Button
                      className="bg-[#2a56b9] text-[white]"
                      icon={<LuAlarmClock className="text-[18px]" />}
                      onClick={() => setIsMailScheduleModal(true)}
                    />
                  </Tooltip>
                  <div className="font-semibold">
                    {formatShortTime(schedule?.time)}
                  </div>
                  {schedule?.time && (
                    <button
                      onClick={() =>
                        setSchedule({
                          ...schedule,
                          time: "",
                          isUpdateScheduleTime: true,
                        })
                      }
                    >
                      <BsTrash3Fill className="text-[18px]" color="red" />
                    </button>
                  )}
                </div>

                <div className="flex justify-end gap-[5px]">
                  <Tooltip
                    placement="top"
                    title={"Save draft"}
                    color={"#0091ff"}
                  >
                    <Button
                      className="bg-[#2a56b9] text-[white]"
                      onClick={handleSaveOrUpdateDraftMessage}
                      icon={<BsSave className="text-[18px]" />}
                    />
                  </Tooltip>
                  {selectedConversation?.conversationId && (
                    <Button
                      className="bg-[#2a56b9] text-white"
                      onClick={handleCloseReply}
                      icon={
                        <BsTrash3Fill className="text-[18px]" color="red" />
                      }
                    />
                  )}
                  {schedule?.time ? (
                    <Button
                      className="bg-[#2a56b9] text-[white]"
                      onClick={sendSchedule}
                      disabled={isLoading}
                      icon={
                        <Icon component={SSendIcon} className="text-[25px] " />
                      }
                    >
                      {languageMap?.["chat.mail.btn.schedule_send"] ??
                        "Schedule send"}
                    </Button>
                  ) : (
                    <Button
                      className="bg-[#2a56b9] text-[white]"
                      onClick={sendNotificationV2}
                      disabled={isLoading}
                      icon={
                        <BsFillSendFill
                          className="text-[18px]"
                          color="#30a3e6"
                        />
                      }
                    >
                      {languageMap?.["chat.mail.btn.send"] ?? "Send"}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </FileHandlerWrapper>
        )}
        {isMailScheduleModal && (
          <MailScheduleModal
            onCancel={() => {
              setIsMailScheduleModal(false);
            }}
            open={isMailScheduleModal}
            schedule={schedule}
            setSchedule={setSchedule}
          />
        )}

        {isOpenModalConfirmation && (
          <GeneralModal
            title={languageMap?.["modal.reply.title"] ?? "Close reply"}
            content={
              languageMap?.["modal.reply.content"] ??
              "Closing the reply will discard your text. Do you want to continue?"
            }
            open={isOpenModalConfirmation}
            onConfirm={handleConfirmCloseReply}
            onCancel={() => setIsOpenModalConfirmation(false)}
          />
        )}

        {isDeleteMailDraft && (
          <GeneralModal
            title="Delete Mail Draft"
            content="Do you want delete mail draft?"
            open={isDeleteMailDraft}
            onConfirm={handleDeleteMailDraft}
            onCancel={() => setIsDeleteMailDraft(false)}
          />
        )}
        {isOpenUploadModal && (
          <PreviewFileUploadModal
            isModalOpen={isOpenUploadModal}
            handleCancel={handleCancelUpload}
            uploadFiles={uploadFiles}
            setUploadFiles={setUploadFiles}
            previewFiles={previewFiles}
            setPreviewFiles={setPreviewFiles}
          />
        )}
      </div>
    );
  },
);
export { MailReply };
